<template>
  <div v-if="isOffline" class="cpay">
    <template v-if="instructions">
      <div class="cpay__body">
        <a
          v-if="isPix && instructions.url"
          class="cpay__qr"
          :href="instructions.url"
          target="_blank"
          rel="noopener"
          :aria-label="$t('chargeBox.qrAria')"
        >
          <img :src="instructions.url" :alt="$t('chargeBox.qrAlt')" />
        </a>

        <div class="cpay__main">
          <p class="cpay__title">
            {{ isPix ? $t("chargeBox.payPix") : $t("chargeBox.payBoleto") }}
          </p>
          <p class="cpay__hint">
            {{ isPix ? $t("chargeBox.hintPix") : $t("chargeBox.hintBoleto") }}
          </p>

          <code class="cpay__code" :title="instructions.code">{{
            instructions.code
          }}</code>

          <div class="cpay__actions">
            <button type="button" class="cpay__btn" @click="copyCode">
              <i class="mdi mdi-content-copy" aria-hidden="true"></i>
              {{ isPix ? $t("chargeBox.copyPix") : $t("chargeBox.copyBoleto") }}
            </button>
            <a
              v-if="!isPix && instructions.url"
              class="cpay__btn cpay__btn--ghost"
              :href="instructions.url"
              target="_blank"
              rel="noopener"
            >
              <i class="mdi mdi-file-pdf-box" aria-hidden="true"></i>
              {{ $t("chargeBox.openBoleto") }}
            </a>
          </div>

          <p v-if="validUntil" class="cpay__valid">
            {{ $t("chargeBox.validUntil", { date: validUntil }) }}
          </p>
        </div>
      </div>
    </template>

    <!-- O worker emite o código alguns dias antes do vencimento; até lá a
         mensalidade existe, mas ainda não há o que pagar. -->
    <p v-else class="cpay__soon">
      <i class="mdi mdi-clock-outline" aria-hidden="true"></i>
      {{ isPix ? $t("chargeBox.soonPix") : $t("chargeBox.soonBoleto") }}
    </p>
  </div>
</template>

<script>
import { format } from "date-fns";

export default {
  name: "ChargePaymentBox",

  props: {
    // Mensalidade de GET /customers/finance (com `paymentMethod` e
    // `paymentInstructions`).
    charge: {
      type: Object,
      required: true,
    },
  },

  computed: {
    method() {
      return (
        (this.charge.paymentInstructions &&
          this.charge.paymentInstructions.method) ||
        this.charge.paymentMethod
      );
    },
    isPix() {
      return this.method === "pix";
    },
    isOffline() {
      return this.method === "pix" || this.method === "boleto";
    },
    instructions() {
      return this.charge.paymentInstructions || null;
    },
    validUntil() {
      const raw = this.instructions && this.instructions.expiresAt;
      if (!raw) return "";
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return "";
      return format(date, this.$t("formats.dayMonthTime"));
    },
  },

  methods: {
    async copyCode() {
      const text = this.instructions.code;
      const label = this.isPix
        ? this.$t("chargeBox.copiedPix")
        : this.$t("chargeBox.copiedBoleto");

      try {
        await navigator.clipboard.writeText(text);
      } catch (_error) {
        // Navegador sem a Clipboard API (ou sem permissão): seleção manual.
        const area = document.createElement("textarea");
        area.value = text;
        area.setAttribute("readonly", "");
        area.style.position = "absolute";
        area.style.left = "-9999px";
        document.body.appendChild(area);
        area.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(area);

        if (!copied) {
          this.$root.$emit("display-alert", {
            text: this.$t("chargeBox.copyFailed"),
            type: 1,
          });
          return;
        }
      }

      this.$root.$emit("display-alert", { text: label, type: 0 });
    },
  },
};
</script>

<style scoped>
.cpay {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --paper: var(--dk-paper, #fbf7fa);
  --pink: var(--dk-pink, #e8276f);
  margin-top: 16px;
  /* O quadro vive dentro do card do valor, que encolhe com o menu lateral
     aberto: o QR vai para cima pela largura do proprio quadro. */
  container-type: inline-size;
  container-name: cpay;
}

.cpay__body {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--paper);
}

.cpay__qr {
  flex: none;
  display: block;
  width: 112px;
  height: 112px;
  padding: 6px;
  border-radius: 12px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.cpay__qr img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.cpay__main {
  min-width: 0;
  flex: 1 1 auto;
}
.cpay__title {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--ink);
}
.cpay__hint {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: var(--muted);
}

.cpay__code {
  display: block;
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 0.74rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cpay__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.cpay__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid var(--pink);
  background: var(--pink);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.16s ease;
}
.cpay__btn:hover {
  opacity: 0.9;
}
.cpay__btn:focus-visible {
  outline: 2px solid var(--pink);
  outline-offset: 2px;
}
.cpay__btn--ghost {
  background: var(--dk-surface, #fff);
  color: var(--pink);
}

.cpay__valid {
  margin: 8px 0 0;
  font-size: 0.75rem;
  color: var(--muted);
}

.cpay__soon {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--paper);
  font-size: 0.8rem;
  color: var(--muted);
}

@container cpay (max-width: 420px) {
  .cpay__body {
    flex-direction: column;
    padding: 14px;
  }
  .cpay__qr {
    width: 160px;
    height: 160px;
    align-self: center;
  }
  .cpay__main {
    width: 100%;
  }
  .cpay__btn {
    flex: 1 1 auto;
    justify-content: center;
  }
}

@supports not (container-type: inline-size) {
  @media (max-width: 520px) {
    .cpay__body {
      flex-direction: column;
    }
    .cpay__qr {
      width: 160px;
      height: 160px;
      align-self: center;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .cpay__btn {
    transition: none;
  }
}
</style>
